import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiClient } from '@/lib/api-client';
import { useOrganizationStore } from '@/stores/organizationStore';

/** Paths where auth/me must not run (no session needed; avoids 401 from expired token). */
const PUBLIC_NO_AUTH_PATHS = ['/invite/accept', '/login', '/register', '/forgot-password', '/', '/privacy', '/terms', '/faq'];

/** True when the SPA route does not require an authenticated session (shared with hooks that must not hit protected APIs). */
export function isPublicNoAuthPath(location: string): boolean {
  return (
    PUBLIC_NO_AUTH_PATHS.includes(location) ||
    location.startsWith('/reset-password') ||
    location.startsWith('/verify-email') ||
    location.startsWith('/ticket/view/') ||
    location.startsWith('/ticket/')
  );
}

export function useCurrentUser() {
  const [location] = useLocation();
  const selectedOrganizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const isPublicNoAuth = isPublicNoAuthPath(location);
  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me', selectedOrganizationId ?? 'default'],
    queryFn: () => apiClient.getMe(selectedOrganizationId ?? undefined),
    retry: false,
    staleTime: 0,
    enabled: !isPublicNoAuth,
  });
  const user = (data as any)?.user || null;
  const provisioningStatus = (user as any)?.orgProvisioningStatus as
    | 'pending'
    | 'ready'
    | 'failed'
    | undefined;
  const provisioningError = (user as any)?.orgProvisioningError as string | null | undefined;
  return { user, isLoading, error, raw: data, provisioningStatus, provisioningError } as const;
}

export function useCurrentUserRole() {
  const { user, isLoading, error } = useCurrentUser();
  // Use org-scoped resolved role only; never fall back to global users.role
  const role = (user as any)?.resolvedOrgRole ?? (user as any)?.orgRole ?? null;
  return { role, isLoading, error } as const;
}
